import { useWeb3React } from '@web3-react/core'
import Zksync from 'assets/png/Zksync.png'
import bera from 'assets/png/bera.png'
import bsc from 'assets/png/bsc.png'
import wld from 'assets/png/wld.png'
import close from 'assets/svg/close.svg'
import clsx from 'clsx'
import { ButtonV3 } from 'components/Button/ButtonV3'
import { useFormContext } from 'react-hook-form'
import { BrigeForm } from './bridge'
import { BERA_OFT, BSC_OFT, WORLDCHAIN_OFT, ZKSYNC_OFT } from './constant'

interface ChainOption {
  chainId: number
  name: string
  img: string
  config: typeof ZKSYNC_OFT
}

const CHAIN_OPTIONS: ChainOption[] = [
  {
    chainId: ZKSYNC_OFT.chainId,
    name: ZKSYNC_OFT.name,
    img: Zksync,
    config: ZKSYNC_OFT,
  },
  {
    chainId: BERA_OFT.chainId,
    name: BERA_OFT.name,
    img: bera,
    config: BERA_OFT,
  },
  {
    chainId: BSC_OFT.chainId,
    name: BSC_OFT.name,
    img: bsc,
    config: BSC_OFT,
  },
  {
    chainId: WORLDCHAIN_OFT.chainId,
    name: WORLDCHAIN_OFT.name,
    img: wld,
    config: WORLDCHAIN_OFT,
  },
]

interface ChainModalProps {
  isOpen: boolean
  onClose: () => void
  type: 'from' | 'to'
  currentFromChain: number
  currentToChain: number
}

export function ChainModal({ isOpen, onClose, type, currentFromChain, currentToChain }: ChainModalProps) {
  const { setValue } = useFormContext<BrigeForm>()
  const { chainId } = useWeb3React()

  if (!isOpen) return null

  const getAvailableChains = () => {
    if (type === 'from') {
      // For 'from' chain, only zkSync and Bera are allowed as source chains
      return CHAIN_OPTIONS.filter((chain) => chain.chainId === ZKSYNC_OFT.chainId || chain.chainId === BERA_OFT.chainId)
    } else {
      // For 'to' chain, available options depend on the selected 'from' chain
      if (currentFromChain === ZKSYNC_OFT.chainId) {
        // zkSync can go to BSC and WLD
        return CHAIN_OPTIONS.filter(
          (chain) => chain.chainId === BSC_OFT.chainId || chain.chainId === WORLDCHAIN_OFT.chainId
        )
      } else if (currentFromChain === BERA_OFT.chainId) {
        // Bera can only go to zkSync
        return CHAIN_OPTIONS.filter((chain) => chain.chainId === ZKSYNC_OFT.chainId)
      }
      return []
    }
  }

  const handleChainSelect = (selectedChain: ChainOption) => {
    if (type === 'from') {
      setValue('from', {
        chainId: selectedChain.chainId,
        name: selectedChain.name,
        img: selectedChain.img,
      })

      // Auto-set appropriate 'to' chain based on routing rules
      if (selectedChain.chainId === ZKSYNC_OFT.chainId) {
        // Default zkSync → BSC (Bera route is disabled)
        setValue('to', {
          chainId: BSC_OFT.chainId,
          name: BSC_OFT.name,
          img: bsc,
        })
      } else if (selectedChain.chainId === BERA_OFT.chainId) {
        // Bera can only go to zkSync
        setValue('to', {
          chainId: ZKSYNC_OFT.chainId,
          name: ZKSYNC_OFT.name,
          img: Zksync,
        })
      }
    } else {
      setValue('to', {
        chainId: selectedChain.chainId,
        name: selectedChain.name,
        img: selectedChain.img,
      })
    }

    setValue('value', '') // Reset amount when chains change
    onClose()
  }

  const availableChains = getAvailableChains()

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-[#0d0d0d] rounded-2xl border border-[#1f1f1f] p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-white text-xl font-medium">Select {type === 'from' ? 'Source' : 'Destination'} Chain</h2>
          <ButtonV3
            onClick={onClose}
            className="w-8 h-8 p-1.5 bg-[#1f1f1f] rounded-lg border border-[#2a2a2a] flex justify-center items-center"
          >
            <img src={close} alt="close" width={16} height={16} />
          </ButtonV3>
        </div>

        <div className="space-y-3">
          {availableChains.map((chain) => {
            const isCurrent =
              (type === 'from' && chain.chainId === currentFromChain) ||
              (type === 'to' && chain.chainId === currentToChain)
            const isConnected = chainId === chain.chainId

            return (
              <button
                key={chain.chainId}
                onClick={() => handleChainSelect(chain)}
                disabled={isCurrent}
                className={clsx(
                  'w-full p-4 rounded-xl border transition-colors flex items-center gap-3',
                  isCurrent
                    ? 'bg-[#1f1f1f] border-[#2a2a2a] opacity-50 cursor-not-allowed'
                    : 'bg-[#141414] border-[#1f1f1f] hover:bg-[#1a1a1a] hover:border-[#2a2a2a]'
                )}
              >
                <div className="w-10 h-10 flex-shrink-0">
                  <img src={chain.img} className="w-full h-full object-cover rounded-full" alt={chain.name} />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-white text-base font-medium">{chain.name}</p>
                  {isConnected && <p className="text-green-400 text-sm">Connected</p>}
                </div>
                {isCurrent && <div className="w-3 h-3 rounded-full bg-green-400"></div>}
              </button>
            )
          })}
        </div>

        {availableChains.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-400">No available chains for this selection</p>
          </div>
        )}
      </div>
    </div>
  )
}
